import { formatAgeInDays } from '@/features/text-mode/utils/text-utils';
import type { EvidenceCategory, EvidenceItem, QuestionItem } from '@/features/text-mode/types/text-mode.types';
import type { PatientInfo } from '../types/overall-assessment.types';

export const UNKNOWN_TOKEN = 'app.assessment_mode.text.input.option.unknown';

type Translate = (token: string) => string;
type FormatNumber = (value: number) => string;
type ReviewValue = string | number | boolean | null | undefined;

const DEFAULT_TIME_LABELS: Record<string, string> = {
  'partials.time.day': 'day',
  'partials.time.days': 'days',
  'partials.time.month': 'month',
  'partials.time.months': 'months',
  'partials.time.year': 'year',
  'partials.time.years': 'years',
};

const defaultTranslate: Translate = function defaultTranslate(token: string): string {
  return DEFAULT_TIME_LABELS[token] || token;
};
const defaultFormatNumber: FormatNumber = String;

export function normalizeReviewValue(
  value: ReviewValue,
): string | number | boolean | null {
  return value === undefined || value === '' ? null : value;
}

export function formatAge(
  value: ReviewValue,
  unknownLabel: string,
): string {
  if (value === null || value === undefined || value === '') return unknownLabel;

  const rawAge = String(value);
  if (/\b(year|years|month|months|day|days|yr|yrs|mo|mos)\b/i.test(rawAge)) return rawAge;

  return `${rawAge} years`;
}

export function getItemValue(
  categories: EvidenceCategory[],
  itemId: string,
): string | number | boolean | null | undefined {
  return getItem(categories, itemId)?.value;
}

export function getItem(
  categories: EvidenceCategory[],
  itemId: string,
): EvidenceItem | undefined {
  for (const category of categories) {
    const item = category.items.find((candidate) => candidate.id === itemId);
    if (item) return item;
  }

  return undefined;
}

function formatAgeFromDays(
  value: string | number | boolean | null | undefined,
  translate: Translate,
  formatNumber: FormatNumber,
): string {
  if (typeof value !== 'number' && typeof value !== 'string') return '';

  const parsedAgeInDays = Number(value);
  if (!Number.isFinite(parsedAgeInDays) || parsedAgeInDays < 0) return '';

  return formatAgeInDays(parsedAgeInDays, translate, formatNumber);
}

function formatPatientAgeItem(
  item: EvidenceItem | undefined,
  unknownLabel: string,
  translate: Translate,
  formatNumber: FormatNumber,
): string {
  const value = item?.value;
  if (value === null || value === undefined || value === '') return '';

  if (item?.valueFormatter === 'age') {
    return formatAgeFromDays(value, translate, formatNumber) || unknownLabel;
  }

  return formatAge(value, unknownLabel);
}

export function buildReviewEvidenceCategories(
  categories: EvidenceCategory[],
  questions: QuestionItem[],
  patientId: string | number | null,
): EvidenceCategory[] {
  const reviewCategories = categories.map((category: EvidenceCategory) => ({
    ...category,
    items: [
      ...(category.id === 'patient' && !category.items.some((item: EvidenceItem) => item.id === 'patient-id')
        ? [{
          id: 'patient-id',
          name: 'Patient ID',
          nameToken: 'app.assessment_mode.text.input.patient_id_label',
          value: patientId,
          editable: false,
          readOnly: true,
        }]
        : []),
      ...category.items.map((item: EvidenceItem) => ({
        ...item,
        value: normalizeReviewValue(item.value),
        editable: false,
        readOnly: true,
      })),
    ],
  }));

  const questionItems = questions.map((question: QuestionItem) => ({
    id: `follow-up-question-${question.id}`,
    name: question.prompt,
    nameToken: question.promptToken,
    value: normalizeReviewValue(question.answer),
    editable: false,
    readOnly: true,
  }));

  if (questionItems.length > 0) {
    reviewCategories.push({
      id: 'follow-up-questions',
      title: 'app.assessment_mode.text.input.follow_up_questions_header',
      items: questionItems,
    });
  }

  return reviewCategories;
}

interface BuildPatientInfoArgs {
  evidenceCategories: EvidenceCategory[];
  patientId?: string | number | null;
  ageInDays?: string | number;
  isMaleSelected?: boolean;
  isFemaleSelected?: boolean;
  unknownLabel: string;
  translate?: Translate;
  formatNumber?: FormatNumber;
}

export function buildPatientInfo({
  evidenceCategories,
  patientId,
  ageInDays,
  isMaleSelected,
  isFemaleSelected,
  unknownLabel,
  translate = defaultTranslate,
  formatNumber = defaultFormatNumber,
}: BuildPatientInfoArgs): PatientInfo {
  const evidencePatientId = getItemValue(evidenceCategories, 'patient-id');
  const evidenceAgeItem = getItem(evidenceCategories, 'patient-age');
  const evidenceGender = getItemValue(evidenceCategories, 'patient-gender');

  let rawGender = '';
  if (isMaleSelected) {
    rawGender = 'male';
  } else if (isFemaleSelected) {
    rawGender = 'female';
  }

  const genderFromPanel = rawGender
    ? rawGender.charAt(0).toUpperCase() + rawGender.slice(1)
    : '';
  const ageFromEvidence = formatPatientAgeItem(evidenceAgeItem, unknownLabel, translate, formatNumber);
  const ageFromPanel = formatAgeFromDays(ageInDays, translate, formatNumber);
  const gender = evidenceGender ? String(evidenceGender) : genderFromPanel || unknownLabel;
  const age = ageFromEvidence || ageFromPanel || unknownLabel;
  const selectedPatientId = patientId || evidencePatientId;

  return {
    id: selectedPatientId ? String(selectedPatientId) : unknownLabel,
    gender,
    age,
  };
}
